import { useCallback } from 'react';
import type { CustomFieldValue } from '../Generic/customFieldValue';
import { atom, useAtomValue } from 'jotai';
import {
  AddAdultToFamilyCommand,
  AddChildToFamilyCommand,
  AddPersonAddress,
  AddPersonEmailAddress,
  AddPersonPhoneNumber,
  Address,
  Age,
  CreateVolunteerFamilyWithNewAdultCommand,
  CustodialRelationship,
  EmailAddress,
  EmailAddressType,
  FamilyAdultRelationshipInfo,
  Gender,
  PersonCommand,
  PhoneNumber,
  PhoneNumberType,
  UpdatePersonAddress,
  UpdatePersonConcerns,
  UpdatePersonEmailAddress,
  UpdatePersonName,
  UpdatePersonNotes,
  UpdatePersonPhoneNumber,
  NoteCommand,
  CreateDraftNote,
  EditDraftNote,
  ApproveNote,
  DiscardDraftNote,
  CommunityRecordsAggregate,
  CreatePartneringFamilyWithNewAdultCommand,
  FamilyCommand,
  UploadFamilyDocument,
  UndoCreatePerson,
  DeleteUploadedFamilyDocument,
  UpdatePersonGender,
  UpdatePersonAge,
  UpdatePersonEthnicity,
  Note,
  UpdateAdultRelationshipToFamily,
  CustodialRelationshipType,
  UpdateCustodialRelationshipType,
  RemoveCustodialRelationship,
  RemovePersonEmailAddress,
  RemovePersonPhoneNumber,
  ChangePrimaryFamilyContact,
  FamilyRecordsCommand,
  PersonRecordsCommand,
  NoteRecordsCommand,
  CustomField,
  UpdateCustomFamilyField,
  CommunityCommand,
  CommunityRecordsCommand,
  ConvertChildToAdult,
  UndoCreateFamily,
  UpdateNoteAccessLevel,
  PinNote,
  UnpinNote,
  UpdateTestFamilyFlag,
  Person,
  UpdateCustomFamilyMemberField,
} from '../GeneratedClient';
import {
  mapLoadedValue,
  useAtomicRecordsCommandCallback,
  useCompositeRecordsCommandCallback,
  visibleAggregatesState,
  visibleFamiliesAtom,
} from './Data';
import { commandFactory } from './CommandFactory';
import { SYSTEM_USER_ID } from '../constants';
import posthog from 'posthog-js';

const systemPerson = Person.fromJS({
  id: SYSTEM_USER_ID,
  active: true,
  firstName: 'SYSTEM',
  lastName: '',
  addresses: [],
  phoneNumbers: [],
  emailAddresses: [],
});

const isSystemUserId = (id?: string) => id?.toLowerCase() === SYSTEM_USER_ID;
const noteAuthorLookupErrorsTracked = new Set<string>();

const familyByIdAtom = atom((get) => {
  const visibleFamilies = get(visibleFamiliesAtom);

  return mapLoadedValue(
    visibleFamilies,
    (families) =>
      new Map(
        families.flatMap((family) =>
          family.family?.id ? [[family.family.id, family] as const] : []
        )
      )
  );
});

const personAndFamilyByPersonIdAtom = atom((get) => {
  const visibleFamilies = get(visibleFamiliesAtom);

  return mapLoadedValue(
    visibleFamilies,
    (families) =>
      new Map(
        families.flatMap((family) => [
          ...(family.family?.adults?.flatMap((adult) =>
            adult.item1?.id
              ? [
                  [
                    adult.item1.id,
                    { family: family.family, person: adult.item1 },
                  ] as const,
                ]
              : []
          ) ?? []),
          ...(family.family?.children?.flatMap((child) =>
            child.id
              ? [[child.id, { family: family.family, person: child }] as const]
              : []
          ) ?? []),
        ])
      )
  );
});

const userPersonByIdAtom = atom((get) => {
  const visibleFamilies = get(visibleFamiliesAtom);

  return mapLoadedValue(visibleFamilies, (families) => {
    const userPersonById = new Map<string | undefined, Person | undefined>();

    families.forEach((family) => {
      family.users?.forEach((user) => {
        if (userPersonById.has(user.userId)) return;

        userPersonById.set(
          user.userId,
          family.family?.adults?.find(
            (adult) => adult.item1?.id === user.personId
          )?.item1
        );
      });
    });

    return userPersonById;
  });
});

const communityByIdAtom = atom((get) => {
  const visibleAggregates = get(visibleAggregatesState);

  return mapLoadedValue(
    visibleAggregates,
    (aggregates) =>
      new Map(
        aggregates.flatMap((aggregate) =>
          aggregate instanceof CommunityRecordsAggregate &&
          aggregate.community?.community?.id
            ? [[aggregate.community.community.id, aggregate.community] as const]
            : []
        )
      )
  );
});

function trackNoteAuthorLookupError(note: Note, reason: string) {
  const eventKey = `${note.id ?? 'unknown'}:${reason}:${note.authorPersonId ?? 'none'}:${note.authorUserId ?? 'none'}`;
  if (noteAuthorLookupErrorsTracked.has(eventKey)) {
    return;
  }

  noteAuthorLookupErrorsTracked.add(eventKey);

  posthog.captureException(new Error(reason), {
    $exception_level: 'warning',
    reason,
    noteId: note.id,
    noteStatus: note.status,
    noteAuthorPersonId: note.authorPersonId,
    noteAuthorUserId: note.authorUserId,
  });
}

export function usePersonLookup() {
  const familyById = useAtomValue(familyByIdAtom);

  return useCallback(
    (familyId?: string, personId?: string) => {
      const family = familyId ? familyById.get(familyId) : undefined;
      const adult = family?.family?.adults?.find(
        (adult) => adult.item1!.id === personId
      );
      const person =
        adult?.item1 ||
        family?.family?.children?.find((child) => child.id === personId);
      return person;
    },
    [familyById]
  );
}

export function usePersonAndFamilyLookup() {
  const personAndFamilyByPersonId = useAtomValue(personAndFamilyByPersonIdAtom);

  return useCallback(
    (personId?: string) =>
      (personId ? personAndFamilyByPersonId.get(personId) : undefined) ?? {
        family: undefined,
        person: undefined,
      },
    [personAndFamilyByPersonId]
  );
}

export function useUserLookup() {
  const userPersonById = useAtomValue(userPersonByIdAtom);

  return useCallback(
    (userId?: string) => {
      if (isSystemUserId(userId)) {
        return systemPerson;
      }

      return userPersonById.get(userId);
    },
    [userPersonById]
  );
}

export function useNoteAuthorLookup() {
  const personAndFamilyLookup = usePersonAndFamilyLookup();
  const userLookup = useUserLookup();

  return (note?: Note) => {
    if (!note) return undefined;
    if (!note.authorPersonId) {
      const userAuthor = userLookup(note.authorUserId);
      if (!userAuthor) {
        trackNoteAuthorLookupError(note, 'missing-author-with-user-id-only');
      }
      return userAuthor;
    }

    if (isSystemUserId(note.authorPersonId)) return systemPerson;

    const personAuthor = personAndFamilyLookup(note.authorPersonId).person;
    if (personAuthor) return personAuthor;

    const userAuthor = userLookup(note.authorUserId);
    if (userAuthor) {
      trackNoteAuthorLookupError(
        note,
        'missing-author-person-fell-back-to-user-id'
      );
      return userAuthor;
    }

    trackNoteAuthorLookupError(note, 'missing-author-for-person-and-user-id');
    return undefined;
  };
}

export function useFamilyLookup() {
  const familyById = useAtomValue(familyByIdAtom);

  return useCallback(
    (familyId?: string) => (familyId ? familyById.get(familyId) : undefined),
    [familyById]
  );
}

export function useCommunityLookup() {
  const communityById = useAtomValue(communityByIdAtom);

  return useCallback(
    (communityId?: string) =>
      communityId ? communityById.get(communityId) : undefined,
    [communityById]
  );
}
function useFamilyCommandCallback<T extends unknown[]>(
  callback: (familyId: string, ...args: T) => Promise<FamilyCommand>
) {
  return useAtomicRecordsCommandCallback(async (familyId, ...args: T) => {
    const command = new FamilyRecordsCommand();
    command.command = await callback(familyId, ...args);
    return command;
  });
}

function usePersonCommandCallback<T extends unknown[]>(
  callback: (familyId: string, ...args: T) => Promise<PersonCommand>
) {
  return useAtomicRecordsCommandCallback(async (familyId, ...args: T) => {
    const command = new PersonRecordsCommand();
    command.command = await callback(familyId, ...args);
    command.familyId = familyId;
    return command;
  });
}

function useNoteCommandCallback<T extends unknown[]>(
  callback: (familyId: string, ...args: T) => Promise<NoteCommand>
) {
  return useAtomicRecordsCommandCallback(async (familyId, ...args: T) => {
    const command = new NoteRecordsCommand();
    command.command = await callback(familyId, ...args);
    return command;
  });
}

export function useCommunityCommand<
  TCommand extends CommunityCommand,
  TArgs extends unknown[],
>(callback: (communityId: string, ...args: TArgs) => TCommand) {
  return useAtomicRecordsCommandCallback(
    async (communityId, ...args: TArgs) => {
      const command = new CommunityRecordsCommand();
      command.command = callback(communityId, ...args);
      return command;
    }
  );
}

export function useDirectoryModel() {
  const undoCreateFamily = useFamilyCommandCallback(async (familyId) => {
    const command = commandFactory(UndoCreateFamily, {
      familyId: familyId,
    });
    return command;
  });
  const uploadFamilyDocument = useFamilyCommandCallback(
    async (familyId, uploadedDocumentId: string, uploadedFileName: string) => {
      const command = commandFactory(UploadFamilyDocument, {
        familyId: familyId,
        uploadedDocumentId: uploadedDocumentId,
        uploadedFileName: uploadedFileName,
      });

      return command;
    }
  );
  const deleteUploadedFamilyDocument = useFamilyCommandCallback(
    async (familyId, uploadedDocumentId: string) => {
      const command = commandFactory(DeleteUploadedFamilyDocument, {
        familyId: familyId,
        uploadedDocumentId: uploadedDocumentId,
      });
      return command;
    }
  );
  const convertChildToAdult = useFamilyCommandCallback(
    async (
      familyId,
      personId: string,
      newRelationship: FamilyAdultRelationshipInfo
    ) => {
      const command = commandFactory(ConvertChildToAdult, {
        familyId: familyId,
        personId: personId,
        newRelationshipToFamily: newRelationship,
      });
      return command;
    }
  );
  const updateAdultRelationshipToFamily = useFamilyCommandCallback(
    async (
      familyId,
      adultPersonId: string,
      relationship: FamilyAdultRelationshipInfo
    ) => {
      const command = commandFactory(UpdateAdultRelationshipToFamily, {
        familyId: familyId,
        adultPersonId: adultPersonId,
        relationshipToFamily: relationship,
      });
      return command;
    }
  );
  const upsertCustodialRelationship = useFamilyCommandCallback(
    async (
      familyId,
      childId: string,
      adultId: string,
      type: CustodialRelationshipType
    ) => {
      const command = commandFactory(UpdateCustodialRelationshipType, {
        familyId: familyId,
        childPersonId: childId,
        adultPersonId: adultId,
        type: type,
      });
      return command;
    }
  );
  const removeCustodialRelationship = useFamilyCommandCallback(
    async (familyId, childId: string, adultId: string) => {
      const command = commandFactory(RemoveCustodialRelationship, {
        familyId: familyId,
        childPersonId: childId,
        adultPersonId: adultId,
      });
      return command;
    }
  );
  const updatePrimaryFamilyContact = useFamilyCommandCallback(
    async (familyId, adultId: string) => {
      const command = commandFactory(ChangePrimaryFamilyContact, {
        familyId: familyId,
        adultId: adultId,
      });
      return command;
    }
  );
  const updateCustomFamilyField = useFamilyCommandCallback(
    async (
      familyId: string,
      customField: CustomField,
      value: CustomFieldValue
    ) => {
      const command = commandFactory(UpdateCustomFamilyField, {
        familyId: familyId,
        completedCustomFieldId: crypto.randomUUID(),
        customFieldName: customField.name,
        customFieldType: customField.type,
        value: value,
      });
      return command;
    }
  );
  const updateCustomFamilyMemberField = usePersonCommandCallback(
    async (
      _familyId: string,
      personId: string,
      customField: CustomField,
      value: CustomFieldValue
    ) => {
      const command = commandFactory(UpdateCustomFamilyMemberField, {
        personId: personId,
        completedCustomFieldId: crypto.randomUUID(),
        customFieldName: customField.name,
        customFieldType: customField.type,
        value: value,
      });
      return command;
    }
  );
  const updateTestFamilyFlag = useFamilyCommandCallback(
    async (familyId, isTestFamily: boolean) => {
      const command = commandFactory(UpdateTestFamilyFlag, {
        familyId: familyId,
        isTestFamily: isTestFamily,
      });
      return command;
    }
  );
  const updatePersonName = usePersonCommandCallback(
    async (
      _familyId,
      personId: string,
      firstName: string,
      lastName: string
    ) => {
      const command = commandFactory(UpdatePersonName, {
        personId: personId,
        firstName: firstName,
        lastName: lastName,
      });
      return command;
    }
  );
  const updatePersonGender = usePersonCommandCallback(
    async (_familyId, personId: string, gender: Gender) => {
      const command = commandFactory(UpdatePersonGender, {
        personId: personId,
        gender: gender,
      });
      return command;
    }
  );
  const updatePersonAge = usePersonCommandCallback(
    async (_familyId, personId: string, age: Age) => {
      const command = commandFactory(UpdatePersonAge, {
        personId: personId,
        age: age,
      });
      return command;
    }
  );
  const updatePersonEthnicity = usePersonCommandCallback(
    async (_familyId, personId: string, ethnicity: string) => {
      const command = commandFactory(UpdatePersonEthnicity, {
        personId: personId,
        ethnicity: ethnicity,
      });
      return command;
    }
  );
  const updatePersonConcerns = usePersonCommandCallback(
    async (_familyId, personId: string, concerns: string | null) => {
      const command = commandFactory(UpdatePersonConcerns, {
        personId: personId,
        concerns: concerns || undefined,
      });
      return command;
    }
  );
  const updatePersonNotes = usePersonCommandCallback(
    async (_familyId, personId: string, notes: string | null) => {
      const command = commandFactory(UpdatePersonNotes, {
        personId: personId,
        notes: notes || undefined,
      });
      return command;
    }
  );
  const undoCreatePerson = usePersonCommandCallback(
    async (_familyId, personId: string) => {
      const command = commandFactory(UndoCreatePerson, {
        personId: personId,
      });
      return command;
    }
  );
  const addPersonPhoneNumber = usePersonCommandCallback(
    async (
      _familyId,
      personId: string,
      phoneNumber: string,
      phoneType: PhoneNumberType,
      isPreferred: boolean
    ) => {
      const command = commandFactory(AddPersonPhoneNumber, {
        personId: personId,
        phoneNumber: commandFactory(PhoneNumber, {
          id: crypto.randomUUID(),
          number: phoneNumber,
          type: phoneType,
        }),
        isPreferredPhoneNumber: isPreferred,
      });
      return command;
    }
  );
  const updatePersonPhoneNumber = usePersonCommandCallback(
    async (
      _familyId,
      personId: string,
      phoneId: string,
      phoneNumber: string,
      phoneType: PhoneNumberType,
      isPreferred: boolean
    ) => {
      const command = commandFactory(UpdatePersonPhoneNumber, {
        personId: personId,
        phoneNumber: commandFactory(PhoneNumber, {
          id: phoneId,
          number: phoneNumber,
          type: phoneType,
        }),
        isPreferredPhoneNumber: isPreferred,
      });
      return command;
    }
  );
  const removePersonPhoneNumber = usePersonCommandCallback(
    async (_familyId, personId: string, phoneNumberId: string) => {
      return commandFactory(RemovePersonPhoneNumber, {
        personId,
        phoneNumberId,
      });
    }
  );
  const addPersonEmailAddress = usePersonCommandCallback(
    async (
      _familyId,
      personId: string,
      emailAddress: string,
      phoneType: EmailAddressType,
      isPreferred: boolean
    ) => {
      const command = commandFactory(AddPersonEmailAddress, {
        personId: personId,
        emailAddress: commandFactory(EmailAddress, {
          id: crypto.randomUUID(),
          address: emailAddress,
          type: phoneType,
        }),
        isPreferredEmailAddress: isPreferred,
      });
      return command;
    }
  );
  const updatePersonEmailAddress = usePersonCommandCallback(
    async (
      _familyId,
      personId: string,
      phoneId: string,
      emailAddress: string,
      phoneType: EmailAddressType,
      isPreferred: boolean
    ) => {
      const command = commandFactory(UpdatePersonEmailAddress, {
        personId: personId,
        emailAddress: commandFactory(EmailAddress, {
          id: phoneId,
          address: emailAddress,
          type: phoneType,
        }),
        isPreferredEmailAddress: isPreferred,
      });
      return command;
    }
  );
  const removePersonEmailAddress = usePersonCommandCallback(
    async (_familyId, personId: string, emailAddressId: string) => {
      return commandFactory(RemovePersonEmailAddress, {
        personId,
        emailAddressId,
      });
    }
  );
  const addPersonAddress = usePersonCommandCallback(
    async (
      _familyId,
      personId: string,
      address: Address,
      isCurrent: boolean
    ) => {
      const command = commandFactory(AddPersonAddress, {
        personId: personId,
        address: address,
        isCurrentAddress: isCurrent,
      });
      return command;
    }
  );
  const updatePersonAddress = usePersonCommandCallback(
    async (
      _familyId,
      personId: string,
      address: Address,
      isCurrent: boolean
    ) => {
      const command = commandFactory(UpdatePersonAddress, {
        personId: personId,
        address: address,
        isCurrentAddress: isCurrent,
      });
      return command;
    }
  );
  const addAdult = useCompositeRecordsCommandCallback(
    async (
      familyId,
      firstName: string,
      lastName: string,
      gender: Gender | null,
      age: Age | null,
      ethnicity: string | null,
      isInHousehold: boolean,
      relationshipToFamily: string,
      address: Address | null,
      phoneNumber: string | null,
      phoneType: PhoneNumberType,
      emailAddress: string | null,
      emailType: EmailAddressType,
      notes?: string,
      concerns?: string
    ) => {
      const command = commandFactory(AddAdultToFamilyCommand, {
        familyId: familyId,
        personId: crypto.randomUUID(),
        firstName: firstName,
        lastName: lastName,
        gender: gender ?? undefined,
        age: age ?? undefined,
        ethnicity: ethnicity ?? undefined,
        concerns: concerns,
        notes: notes,
        familyAdultRelationshipInfo: commandFactory(
          FamilyAdultRelationshipInfo,
          {
            isInHousehold: isInHousehold,
            relationshipToFamily: relationshipToFamily,
          }
        ),
        address: address ?? undefined,
        phoneNumber: phoneNumber
          ? commandFactory(PhoneNumber, {
              id: crypto.randomUUID(),
              number: phoneNumber,
              type: phoneType,
            })
          : undefined,
        emailAddress: emailAddress
          ? commandFactory(EmailAddress, {
              id: crypto.randomUUID(),
              address: emailAddress,
              type: emailType,
            })
          : undefined,
      });

      return command;
    }
  );
  const addChild = useCompositeRecordsCommandCallback(
    async (
      familyId,
      firstName: string,
      lastName: string,
      gender: Gender | null,
      age: Age | null,
      ethnicity: string | null,
      custodialRelationships: CustodialRelationship[],
      notes?: string,
      concerns?: string
    ) => {
      const personId = crypto.randomUUID();

      const command = commandFactory(AddChildToFamilyCommand, {
        familyId: familyId,
        personId: personId,
        firstName: firstName,
        lastName: lastName,
        gender: gender ?? undefined,
        age: age ?? undefined,
        ethnicity: ethnicity ?? undefined,
        custodialRelationships: custodialRelationships.map((cr) => {
          cr.childId = personId;
          return cr;
        }),
        concerns: concerns,
        notes: notes,
      });
      return command;
    }
  );
  const createVolunteerFamilyWithNewAdult = useCompositeRecordsCommandCallback(
    async (
      familyId: string,
      firstName: string,
      lastName: string,
      gender: Gender | null,
      age: Age | null,
      ethnicity: string | null,
      isInHousehold: boolean,
      relationshipToFamily: string,
      address: Address | null,
      phoneNumber: string | null,
      phoneType: PhoneNumberType,
      emailAddress: string | null,
      emailType: EmailAddressType,
      notes?: string,
      concerns?: string
    ) => {
      const command = commandFactory(CreateVolunteerFamilyWithNewAdultCommand, {
        familyId: familyId,
        personId: crypto.randomUUID(),
        firstName: firstName,
        lastName: lastName,
        gender: gender == null ? undefined : gender,
        age: age == null ? undefined : age,
        ethnicity: ethnicity || undefined,
        concerns: concerns,
        notes: notes,
        familyAdultRelationshipInfo: commandFactory(
          FamilyAdultRelationshipInfo,
          {
            isInHousehold: isInHousehold,
            relationshipToFamily: relationshipToFamily,
          }
        ),
        address: address == null ? undefined : address,
        phoneNumber: phoneNumber
          ? commandFactory(PhoneNumber, {
              id: crypto.randomUUID(),
              number: phoneNumber,
              type: phoneType,
            })
          : undefined,
        emailAddress: emailAddress
          ? commandFactory(EmailAddress, {
              id: crypto.randomUUID(),
              address: emailAddress,
              type: emailType,
            })
          : undefined,
      });
      return command;
    }
  );
  const createPartneringFamilyWithNewAdult = useCompositeRecordsCommandCallback(
    async (
      familyId: string,
      v1CaseOpenedAtUtc: Date,
      firstName: string,
      lastName: string,
      gender: Gender | null,
      age: Age | null,
      ethnicity: string | null,
      isInHousehold: boolean,
      relationshipToFamily: string | null,
      address: Address | null,
      phoneNumber: string | null,
      phoneType: PhoneNumberType,
      emailAddress: string | null,
      emailType: EmailAddressType,
      notes?: string,
      concerns?: string
    ) => {
      const command = commandFactory(
        CreatePartneringFamilyWithNewAdultCommand,
        {
          familyId: familyId,
          personId: crypto.randomUUID(),
          referralId: crypto.randomUUID(),
          referralOpenedAtUtc: v1CaseOpenedAtUtc,
          firstName: firstName,
          lastName: lastName,
          gender: gender == null ? undefined : gender,
          age: age == null ? undefined : age,
          ethnicity: ethnicity || undefined,
          concerns: concerns,
          notes: notes,
          familyAdultRelationshipInfo: commandFactory(
            FamilyAdultRelationshipInfo,
            {
              isInHousehold: isInHousehold,
              relationshipToFamily: relationshipToFamily ?? undefined,
            }
          ),
          address: address == null ? undefined : address,
          phoneNumber: phoneNumber
            ? commandFactory(PhoneNumber, {
                id: crypto.randomUUID(),
                number: phoneNumber,
                type: phoneType,
              })
            : undefined,
          emailAddress: emailAddress
            ? commandFactory(EmailAddress, {
                id: crypto.randomUUID(),
                address: emailAddress,
                type: emailType,
              })
            : undefined,
        }
      );
      return command;
    }
  );
  const createDraftNote = useNoteCommandCallback(
    async (
      familyId,
      noteId: string,
      draftNoteContents: string,
      backdatedTimestampLocal?: Date,
      accessLevel?: string
    ) => {
      const command = commandFactory(CreateDraftNote, {
        familyId: familyId,
        noteId: noteId,
        draftNoteContents: draftNoteContents,
        backdatedTimestampUtc: backdatedTimestampLocal,
        accessLevel: accessLevel,
      });
      return command;
    }
  );
  const editDraftNote = useNoteCommandCallback(
    async (
      familyId,
      noteId: string,
      draftNoteContents: string,
      backdatedTimestampLocal?: Date,
      accessLevel?: string
    ) => {
      const command = commandFactory(EditDraftNote, {
        familyId: familyId,
        noteId: noteId,
        draftNoteContents: draftNoteContents,
        backdatedTimestampUtc: backdatedTimestampLocal,
        accessLevel: accessLevel,
      });
      return command;
    }
  );
  const discardDraftNote = useNoteCommandCallback(
    async (familyId, noteId: string) => {
      const command = commandFactory(DiscardDraftNote, {
        familyId: familyId,
        noteId: noteId,
      });
      return command;
    }
  );
  const approveNote = useNoteCommandCallback(
    async (
      familyId,
      noteId: string,
      finalizedNoteContents: string,
      backdatedTimestampLocal?: Date,
      accessLevel?: string
    ) => {
      const command = commandFactory(ApproveNote, {
        familyId: familyId,
        noteId: noteId,
        finalizedNoteContents: finalizedNoteContents,
        backdatedTimestampUtc: backdatedTimestampLocal,
        accessLevel: accessLevel,
      });
      return command;
    }
  );
  const updateNoteAccessLevel = useNoteCommandCallback(
    async (familyId: string, noteId: string, accessLevel?: string | null) => {
      const command = commandFactory(UpdateNoteAccessLevel, {
        familyId,
        noteId,
        accessLevel: accessLevel ?? undefined,
      });
      return command;
    }
  );

  const pinNote = useNoteCommandCallback(
    async (familyId: string, noteId: string) => {
      const command = commandFactory(PinNote, {
        familyId,
        noteId,
      });
      return command;
    }
  );

  const unpinNote = useNoteCommandCallback(
    async (familyId: string, noteId: string) => {
      const command = commandFactory(UnpinNote, {
        familyId,
        noteId,
      });
      return command;
    }
  );

  return {
    undoCreateFamily,
    uploadFamilyDocument,
    deleteUploadedFamilyDocument,
    convertChildToAdult,
    updateAdultRelationshipToFamily,
    upsertCustodialRelationship,
    removeCustodialRelationship,
    updatePrimaryFamilyContact,
    updateCustomFamilyField,
    updateCustomFamilyMemberField,
    updatePersonName,
    updatePersonGender,
    updatePersonAge,
    updatePersonEthnicity,
    updatePersonConcerns,
    updatePersonNotes,
    undoCreatePerson,
    addPersonPhoneNumber,
    updatePersonPhoneNumber,
    removePersonPhoneNumber,
    addPersonEmailAddress,
    updatePersonEmailAddress,
    removePersonEmailAddress,
    addPersonAddress,
    updatePersonAddress,
    addAdult,
    addChild,
    createVolunteerFamilyWithNewAdult,
    createPartneringFamilyWithNewAdult,
    createDraftNote,
    editDraftNote,
    discardDraftNote,
    approveNote,
    updateNoteAccessLevel,
    pinNote,
    unpinNote,
    updateTestFamilyFlag,
  };
}
